import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  Plus,
  RefreshCw,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import { CardSkeleton, SkeletonBlock, TableSkeleton } from "@/components/ui/Skeleton";
import StatCard from "@/components/ui/StatCard";
import StatusBadge, { STATUS_META, type MandateStatus } from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { useRetailerSession } from "@/components/feature/retailer/RetailerSessionContext";
import { mandateService, retailerService } from "@/services/apiServices";
import type { Mandate } from "@/types/mandate";
import type { Installment } from "@/types/installment";
import DonutChart, { type DonutSegment } from "./components/DonutChart";
import LineChart, { type SeriesPoint } from "./components/LineChart";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DONUT_COLORS: Record<string, string> = {
  ACTIVE: "#059669",
  PENDING_AUTH: "#D97706",
  CREATED: "#0284C7",
  COMPLETED: "#1E40AF",
  TERMINATED: "#DC2626",
  CANCELLED: "#475569",
  PAUSED: "#D97706",
  HALTED: "#DC2626",
};

interface DashboardMetrics {
  totalMandates: number;
  activeMandates: number;
  pendingAuth: number;
  completedMandates: number;
  cancelledMandates: number;
  collectedThisMonth: number;
  collectedThisMonthCount: number;
  lifetimeCollected: number;
  failedCount: number;
  successRate: number;
  dueTodayCount: number;
  overdueCount: number;
}

interface DashboardData {
  metrics: DashboardMetrics;
  statusBreakdown: { status: string; value: number }[];
  collectionTrend: { label: string; value: number; count: number }[];
  upcomingInstallments: Installment[];
  recentMandates: Mandate[];
  wallet: {
    balance: number;
    status: "HEALTHY" | "LOW" | "BLOCKED";
    creationRate: number;
    lowBalanceThreshold: number;
    pendingTopups: number;
  };
  kyc: { status: string };
}

function formatINR(n: number): string {
  return `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDueDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export default function RetailerDashboard() {
  return (
    <ToastProvider>
      <DashboardInner />
    </ToastProvider>
  );
}

function DashboardInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const { retailerId, profile, isKycApproved, kycStatus } = useRetailerSession();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<DashboardData | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await retailerService.getDashboard(retailerId);
        if (res?.data) setData(res.data);
      } catch (err: any) {
        toast.error(err?.message || "Failed to load your dashboard");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [retailerId, toast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const metrics = data?.metrics;
  const recent = data?.recentMandates ?? [];
  const upcoming = data?.upcomingInstallments ?? [];

  const segments: DonutSegment[] = (data?.statusBreakdown ?? [])
    .filter((s) => s.value > 0)
    .map((s) => ({
      label: s.status as MandateStatus,
      value: s.value,
      color: DONUT_COLORS[s.status] ?? "#475569",
    }));

  const trend: SeriesPoint[] = (data?.collectionTrend ?? []).map((t) => ({ label: t.label, value: t.value }));

  const upcomingValue = upcoming.reduce((s, i) => s + i.amount, 0);

  const [refreshingMandateId, setRefreshingMandateId] = useState<string | null>(null);

  async function handleRefreshMandate(e: React.MouseEvent, row: Mandate) {
    e.stopPropagation();
    const idToSync = row.refNo || row.id;
    if (!idToSync) return;
    setRefreshingMandateId(row.id);
    try {
      const res = await mandateService.syncWithRocketPay(idToSync);
      const updated = res?.data?.mandate || res?.data;
      if (updated && updated.status) {
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            recentMandates: prev.recentMandates.map((m) =>
              m.id === row.id || m.refNo === row.refNo ? { ...m, ...updated, status: updated.status } : m
            ),
          };
        });
        toast.success(`Mandate status updated: ${updated.status.replace("_", " ")}`);
      } else {
        load(true);
        toast.success(res?.message || "Status refreshed from gateway");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh status from gateway");
    } finally {
      setRefreshingMandateId(null);
    }
  }

  function copyAuthLink(m: Mandate) {
    navigator.clipboard
      .writeText(`${window.location.origin}${m.authLink}`)
      .then(() => toast.success("Auth link copied to clipboard"))
      .catch(() => toast.error("Could not copy auth link"));
  }

  const columns: Column<Mandate>[] = [
    {
      key: "refNo",
      header: "Ref No",
      width: "140px",
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/retailer/mandates/${row.refNo}`)}
          className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          {row.refNo}
        </button>
      ),
    },
    {
      key: "customerName",
      header: "Customer",
      width: "190px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.customerName}</p>
          <p className="text-xs text-ink-secondary">{row.customerMobile}</p>
        </div>
      ),
    },
    {
      key: "bankName",
      header: "Bank / UPI",
      width: "170px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{row.bankName || row.upiId || "—"}</p>
          <p className="text-xs text-ink-secondary">{row.accountNumber || ""}</p>
        </div>
      ),
    },
    {
      key: "emiAmount",
      header: "EMI",
      width: "110px",
      align: "right",
      render: (row) => (
        <div className="text-right">
          <p className="text-sm font-medium text-ink">{formatINR(row.emiAmount)}</p>
          <p className="text-xs text-ink-secondary">{row.tenureMonths} months</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "135px",
      render: (row) => {
        const isSyncing = refreshingMandateId === row.id || refreshingMandateId === row.refNo;
        return (
          <div className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <button
              type="button"
              onClick={(e) => handleRefreshMandate(e, row)}
              disabled={isSyncing}
              title="Fetch latest status from gateway"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 shadow-sm transition-all cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${isSyncing ? "animate-spin text-primary-600" : ""}`} />
            </button>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      width: "120px",
      render: (row) => <span className="whitespace-nowrap text-[13px] text-ink">{formatDate(row.createdAt)}</span>,
    },
  ];

  function rowActions(row: Mandate) {
    const pending = row.status === "PENDING_AUTH" || row.status === "CREATED";
    return (
      <>
        <button
          type="button"
          onClick={() => navigate(`/retailer/mandates/${row.refNo}`)}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
        >
          <Eye className="h-4 w-4 text-ink-muted" />
          View Details
        </button>
        {pending && (
          <button
            type="button"
            onClick={() => window.open(row.authLink, "_blank", "noopener,noreferrer")}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
          >
            <ExternalLink className="h-4 w-4 text-ink-muted" />
            Preview Link
          </button>
        )}
        {pending && (
          <button
            type="button"
            onClick={() => copyAuthLink(row)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
          >
            <Link2 className="h-4 w-4 text-ink-muted" />
            Copy Auth Link
          </button>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-ink">Dashboard</h1>
          <p className="mt-1 truncate text-sm text-ink-secondary">
            Welcome back, {profile?.name ?? "Retailer"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => load(true)}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />}
          >
            Refresh
          </Button>
          <div title={!isKycApproved ? "Complete your KYC first" : undefined}>
            <Button
              disabled={!isKycApproved}
              onClick={() => navigate("/retailer/create-mandate")}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Create Mandate
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <CardSkeleton cards={4} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65%_35%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-40" />
              <SkeletonBlock className="mt-6 h-56 w-full" />
            </div>
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <SkeletonBlock className="h-5 w-36" />
              <div className="mt-6 flex justify-center">
                <SkeletonBlock className="h-[180px] w-[180px] rounded-full" />
              </div>
            </div>
          </div>
          <TableSkeleton rows={5} columns={6} />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {data?.wallet && data.wallet.status !== "HEALTHY" && (
            <div className="flex flex-col items-start justify-between gap-3 rounded-[10px] border border-warning/40 bg-warning-bg px-4 py-3 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <p className="text-sm text-ink">
                  {data.wallet.status === "BLOCKED"
                    ? `Your wallet balance is below ${formatINR(data.wallet.creationRate)} — you cannot create new mandates.`
                    : `Low wallet balance (${formatINR(data.wallet.balance)}). Top up to keep creating mandates.`}
                </p>
              </div>
              <Button variant="secondary" onClick={() => navigate("/retailer/wallet")}>
                Top up wallet
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              label="Total Mandates"
              value={`${metrics?.totalMandates ?? 0}`}
              icon={FileText}
              onClick={() => navigate("/retailer/mandates?tab=All")}
            />
            <StatCard
              label="Active Mandates"
              value={`${metrics?.activeMandates ?? 0}`}
              icon={CheckCircle2}
              iconBg="bg-success-bg"
              iconColor="text-success"
              onClick={() => navigate("/retailer/mandates?tab=Active")}
            />
            <button
              type="button"
              onClick={() => navigate("/retailer/mandates?tab=Created")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card transition-all hover:border-primary-400 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Pending Authentication</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">{metrics?.pendingAuth ?? 0}</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-bg text-warning">
                  <Clock className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-secondary">Awaiting customer action</div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/retailer/installments?tab=COLLECTED")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card transition-all hover:border-primary-400 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Collected This Month</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">
                    {formatINR(metrics?.collectedThisMonth ?? 0)}
                  </span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-bg text-success">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-secondary">
                {metrics?.collectedThisMonthCount ?? 0} installments · {metrics?.successRate ?? 0}% success rate
              </div>
            </button>
            <button
              type="button"
              onClick={() => navigate("/retailer/installments?tab=SCHEDULED")}
              className="rounded-[10px] border border-line bg-card p-5 text-left shadow-card transition-all hover:border-primary-400 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Due This Week</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">{upcoming.length}</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Calendar className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-secondary">{formatINR(upcomingValue)} in upcoming debits</div>
            </button>
          </div>

          {(metrics?.overdueCount ?? 0) > 0 && (
            <div className="flex flex-col items-start justify-between gap-3 rounded-[10px] border border-line bg-card px-4 py-3 shadow-card sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                <p className="text-sm text-ink">
                  {metrics?.overdueCount} installment{metrics?.overdueCount === 1 ? "" : "s"} presented to the bank
                  are still awaiting an outcome.
                </p>
              </div>
              <Button variant="secondary" onClick={() => navigate("/retailer/installments")}>
                Review installments
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65%_35%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-ink">Collections</h3>
                  <p className="text-[13px] text-ink-secondary">Amount collected over the last 6 months</p>
                </div>
              </div>
              <div className="mt-5">
                {trend.some((t) => t.value > 0) ? (
                  <LineChart data={trend} />
                ) : (
                  <div className="flex h-[220px] items-center justify-center rounded-lg border border-dashed border-slate-300">
                    <p className="text-sm text-ink-secondary">No collections recorded yet</p>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Status Breakdown</h3>
              {segments.length > 0 ? (
                <>
                  <div className="mt-5 flex justify-center">
                    <DonutChart
                      segments={segments}
                      centerValue={`${metrics?.totalMandates ?? 0}`}
                      centerLabel="Total"
                    />
                  </div>
                  <div className="mt-5 flex flex-col gap-2.5">
                    {segments.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => {
                          const isPending = s.label === "PENDING_AUTH" || s.label === "PENDING" || s.label === "AUTH_PENDING";
                          const target = isPending ? "Created" : (STATUS_META[s.label as MandateStatus]?.label || s.label);
                          navigate(`/retailer/mandates?tab=${target}`);
                        }}
                        className="flex w-full items-center justify-between rounded-md p-1 -mx-1 text-left transition-colors hover:bg-slate-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                          <span className="text-[13px] text-ink">
                            {STATUS_META[s.label as MandateStatus]?.label ?? s.label}
                          </span>
                        </div>
                        <span className="text-[13px] font-medium text-ink-secondary">
                          {s.value} · {Math.round((s.value / (metrics?.totalMandates || 1)) * 100)}%
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="mt-5 flex h-[200px] items-center justify-center rounded-lg border border-dashed border-slate-300">
                  <p className="text-sm text-ink-secondary">No mandates yet</p>
                </div>
              )}
            </div>
          </div>

          {upcoming.length > 0 && (
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-ink">Upcoming Installments</h3>
                  <p className="text-[13px] text-ink-secondary">Scheduled debits over the next 7 days</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/retailer/installments")}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  View All
                </Button>
              </div>
              <div className="flex flex-col divide-y divide-[#F1F5F9]">
                {upcoming.map((i) => (
                  <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{i.customerName}</p>
                      <p className="truncate text-xs text-ink-secondary">
                        {i.refNo} · Installment {i.installmentNumber} of {i.totalInstallments}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-ink">{formatINR(i.amount)}</p>
                      <p className="whitespace-nowrap text-xs text-ink-secondary">{formatDueDate(i.dueDate)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-ink">Recent Mandates</h3>
                <p className="text-[13px] text-ink-secondary">Your most recent mandate requests</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/retailer/mandates")}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                View All
              </Button>
            </div>
            {recent.length === 0 ? (
              <EmptyState
                icon={FileText}
                title={kycStatus === "APPROVED" ? "No mandates yet" : "Complete your KYC to get started"}
                description={
                  kycStatus === "APPROVED"
                    ? "Create your first mandate to start tracking customer authorisations here."
                    : "Once your KYC is approved you can start creating mandates for your customers."
                }
                actionLabel={kycStatus === "APPROVED" ? "Create Mandate" : "Go to KYC"}
                onAction={() =>
                  navigate(kycStatus === "APPROVED" ? "/retailer/create-mandate" : "/retailer/kyc")
                }
              />
            ) : (
              <DataTable
                columns={columns}
                data={recent}
                rowKey={(row) => row.id}
                actions={rowActions}
                onRowClick={(row) => navigate(`/retailer/mandates/${row.refNo}`)}
                pageSize={8}
                maxHeight="440px"
              />
            )}
          </div>

          {data?.wallet && (
            <div
              onClick={() => navigate("/retailer/wallet")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/retailer/wallet");
                }
              }}
              className="rounded-[10px] border border-line bg-card p-5 shadow-card transition-all hover:border-primary-400 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <Wallet className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[13px] text-ink-secondary">Wallet balance</p>
                    <p className="text-xl font-semibold text-ink">{formatINR(data.wallet.balance)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {data.wallet.pendingTopups > 0 && (
                    <span className="whitespace-nowrap rounded-full bg-warning-bg px-2.5 py-1 text-xs font-medium text-amber-700">
                      {data.wallet.pendingTopups} top-up pending
                    </span>
                  )}
                  <Button variant="secondary" onClick={() => navigate("/retailer/wallet")}>
                    Open wallet
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
